<template>
    <template v-if="mode === 'interno'">
        <div v-if="isVideo">
            <video controls autoplay muted loop style="max-height:500px; width: 100%;">
                <source :src="pregunta.imagen" type="video/mp4" />
                Your browser does not support the video tag.
            </video>
        </div>
        <v-img v-else max-height="500" :src="pregunta.imagen" />
    </template>
    <v-card variant="text">
        <v-row>
            <v-col cols="12" sm="10">
                <v-card-title class="text-wrap" style="word-break: break-word">
                    {{ pregunta.texto }}
                </v-card-title>
            </v-col>
            <v-col cols="auto" class="hidden-xs">
                <v-chip color="success" variant="outlined" class="mt-3">
                    <v-tooltip activator="parent">{{ t('Selecciona_una_sola_respuesta') }}</v-tooltip>
                    <v-icon icon="mdi-checkbox-marked" start></v-icon>
                    {{ t('única') }}
                </v-chip>
            </v-col>
        </v-row>
        <v-card-text>
            <v-list v-for="opcion in pregunta.opciones" :key="opcion.id" :disabled="haRespondido">
                <v-list-item :value="opcion.id" @click="seleccionarOpcion(opcion.id)">
                    <v-list-item-title class="text-wrap" style="word-break: break-word">{{ opcion.texto }}
                        <v-chip v-if="haRespondido && opcion.es_correcta"
                            :color="opcion.es_correcta === respuestasSeleccionadas.includes(opcion.id) ? 'green' : 'red'"
                            small>
                            <v-icon>
                                {{ opcion.es_correcta ? 'mdi-check' : 'mdi-close' }}</v-icon>
                        </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="haRespondido && opcion.motivo" class="text-wrap"
                        style="line-clamp: unset !important; -webkit-line-clamp: unset !important; display: block !important; overflow: visible !important; white-space: normal !important; word-break: break-word;">
                        {{ opcion.motivo }}
                    </v-list-item-subtitle>
                </v-list-item>
            </v-list>
        </v-card-text>
    </v-card>
    <template v-if="haRespondido && mode === 'interno'">
        <div class="text-center">
            <v-chip :color="feedbackColor">{{ feedbackText }}</v-chip>
            <v-btn block :disabled="loading" color="primary" class="mt-3" @click="next">
                {{ t('Continua') }}
            </v-btn>
        </div>
    </template>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { axios } from '../axios';

const props = defineProps({
    pregunta: {
        type: Object,
        default: () => ({}),
    },
    mode: {
        type: String,
        default: 'interno',
        // 'interno'  => El componente hace el axios POST a enviar_respuestas/.
        // 'externo'  => El componente emite el evento con los datos, 
        //               y NO hace la petición dentro.
    },
});

const emit = defineEmits(['respuesta-enviada', 'info-respuesta']);

const { t } = useI18n();
const respuestasSeleccionadas = ref([]);
const haRespondido = ref(false);
const loading = ref(false);

// Computed para el texto de feedback
const feedbackText = computed(() => {
    if (!haRespondido.value) return '';
    const selectedId = respuestasSeleccionadas.value[0];
    const opcion = props.pregunta.opciones.find(o => o.id === selectedId);
    return opcion && opcion.es_correcta
        ? t('Fantástico')
        : t('Puedes_mejorar');
});

// Color del chip: verde si es correcta, rojo si no
const feedbackColor = computed(() => {
    if (!haRespondido.value) return undefined;
    const selectedId = respuestasSeleccionadas.value[0];
    const opcion = props.pregunta.opciones.find(o => o.id === selectedId);
    return opcion?.es_correcta ? 'success' : 'error';
});

function seleccionarOpcion(opcionId) {
    respuestasSeleccionadas.value = [opcionId];
    // Siempre que el usuario hace clic en una opción, 
    // enviamos un evento con la info de la respuesta para el "modo externo"
    // (o si el padre quisiera usarlo incluso en modo interno).
    emit('info-respuesta', {
        opcion_respuesta_id: opcionId,
        pregunta_id: props.pregunta.id,
    });

    if (props.mode === 'interno') {
        // Lógica interna de envío
        enviarAlServidor(opcionId);
    } else {
        // Modo externo: no hace axios.post. Marcamos "respondido" localmente
        haRespondido.value = true;
    }
}

async function enviarAlServidor(opcionId) {
    const datosParaEnviar = {
        opcion_respuesta_ids: [opcionId],
    };
    loading.value = true;
    try {
        const respuesta = await axios.post(`enviar_respuestas/`, datosParaEnviar);
        if (respuesta.data.success) {
            haRespondido.value = true;
        } else {
            console.error('Error al enviar respuestas:', respuesta.data.message);
        }
    } catch (error) {
        console.error('Error al realizar la solicitud:', error);
    } finally {
        loading.value = false;
    }
}

function next() {
    // Emitir el evento que indica que la pregunta ha sido contestada y pasamos a la siguiente.
    emit('respuesta-enviada', props.pregunta);
    respuestasSeleccionadas.value = [];
}

// Reiniciamos el estado cada vez que la pregunta cambie
watch(
    () => props.pregunta?.id,
    (newId, oldId) => {
        if (newId !== oldId) {
            haRespondido.value = false;
            respuestasSeleccionadas.value = [];
        }
    }
);

// Check if the file is a video (mp4)
const isVideo = computed(() => {
    if (props.pregunta.value && props.pregunta.value.imagen) {
        try {
            const urlObj = new URL(props.pregunta.value.imagen);
            return urlObj.pathname.toLowerCase().endsWith('.mp4');
        } catch (e) {
            // Fallback if URL parsing fails
            return props.pregunta.value.imagen.toLowerCase().includes('.mp4');
        }
    }
    return false;
});

// Inicializar las respuestas seleccionadas con respuestas previas del usuario
onMounted(() => {
    if (props.pregunta.respuestas_usuario && props.pregunta.respuestas_usuario.length > 0) {
        respuestasSeleccionadas.value = props.pregunta.respuestas_usuario.map(
            (respuesta) => respuesta.opcion_respuesta.id
        );
    }
});
</script>